import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { catchError, filter } from 'rxjs/operators';
import { EMPTY, Observable, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { EntityDetailsMode } from '../../../maintain/maintain-form/maintain-form-mode.enum';
import { EntityGroupDetailsService } from '../entity-group-details.service';
import { FieldConfigDTO, FormColumnConfigDTO, MenuDTO } from '../../../../../system/system-dtos';
import { isObjEmpty } from '../../../../utils/utils';
import { ConfirmationReasonModalComponent } from '../../../../dialogs/confirmation-reason-modal/confirmation-reason-modal.component';

@Component({
  selector: 'app-entity-group-subset',
  templateUrl: './entity-group-subset.component.html',
  styleUrls: ['./entity-group-subset.component.scss'],
  standalone: false,
})
export class EntityGroupSubsetComponent implements OnInit {
  @Input() data: any;

  @Input() config: FormColumnConfigDTO;

  @Input() formGroup: UntypedFormGroup;

  @Input() edit: boolean = true;

  @Input() formConfig: MenuDTO;

  @Input() entityId: string;

  @Input() row: number;

  @Input() column: number;

  private _mode: EntityDetailsMode;

  @Input() refreshSubject: Subject<void>;

  public hasTitle: boolean = true;

  private entityName: string;

  @Output() loadingEmitter = new EventEmitter<boolean>();

  @Output() editingEmitter = new EventEmitter<boolean>();

  public canEdit: boolean = true;

  public nestedData: any;

  public loading: boolean = false;

  rows: FieldConfigDTO[][];

  constructor(
    private service: EntityGroupDetailsService,
    private toastrService: ToastrService,
    private route: ActivatedRoute,
    private domSanitizer: DomSanitizer,
    private dialog: MatDialog
  ) {}

  @Input()
  set mode(value: string) {
    for (const m in EntityDetailsMode) {
      if (EntityDetailsMode[m] === value) {
        this._mode = EntityDetailsMode[m];
        break;
      }
    }
  }

  get mode(): string {
    return this._mode;
  }

  ngOnInit() {
    this.entityName = this.route.snapshot.paramMap.get('entityCode');

    this.nestedData = this.data[this.config.code];
    const data = this.nestedData || {};

    if (this.config.jsEditCondition) {
      this.canEdit = eval(this.config.jsEditCondition);
    }

    this.rows = this.getFieldsToDisplayPerRow();

    // this.refreshSubject._subscribe(_ => )
  }

  getDataForFormField(field: FieldConfigDTO): void {
    const { name } = field;

    const datum = this.data[name];
    if (datum || datum === 0) {
      return datum;
    }

    if (!this.formConfig) {
      return datum;
    }

    const { code } = this.config;
    const datum1 = this.data[code];
    return datum1 ? datum1[name] : datum1;
  }

  getFormConfig(): UntypedFormGroup {
    if (this.config.nested) {
      return this.formGroup.controls[this.config.code] as UntypedFormGroup;
    }
    return this.formGroup;
  }

  startEditing() {
    this.switchForm(EntityDetailsMode.EDIT);
    this.edit = true;
    this.editingEmitter.emit(true);
  }

  stopEditing() {
    this.switchForm(EntityDetailsMode.VIEW);
    this.edit = false;
    this.editingEmitter.emit(false);
  }

  private switchForm(mode: EntityDetailsMode) {
    this.mode = mode;
    this.rows = this.getFieldsToDisplayPerRow();
  }

  doEdit() {
    const body = this.formGroup.getRawValue()[this.config.code];

    if (this.config.requireUpdateReason) {
      this.dialog
        .open(ConfirmationReasonModalComponent, {
          data: {
            note: `Please provide a reason for updating ${this.config.title.toLowerCase()}. It will be added to the player commentary.`,
          },
        })
        .afterClosed()
        .pipe(filter((result): result is { reason: string } => !!result))
        .subscribe((result) => this.submitEdit({ ...body, reason: result.reason }));
      return;
    }

    this.submitEdit(body);
  }

  private submitEdit(body: any) {
    this.loading = true;
    this.loadingEmitter.emit(true);

    const url = this.config.updateUrl.replace('{id}', this.entityId);
    const { multipartForm } = this.config;

    let restRequest: Observable<any>;
    if (multipartForm) {
      restRequest = this.service.submitMultipartForm(body, url, this.formGroup, 'PUT');
    } else {
      restRequest = this.service.performHttpPut(url, body);
    }
    restRequest
      .pipe(
        catchError((e) => {
          this.loading = false;
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.loadingEmitter.emit(false);
        this.loading = false;
        this.stopEditing();
        const target = [this.entityName, `#${this.entityId}`].filter(Boolean).join(' ');
        this.toastrService.success(`${this.config.title} for ${target} has been updated!`);
      });
  }

  getFieldsToDisplay(): FieldConfigDTO[] {
    const { fields } = this.config;
    if (this.mode === EntityDetailsMode.ADD) {
      return fields.filter((f) => !f.editHide && !f.addIgnore);
    }
    if (this.mode === EntityDetailsMode.EDIT) {
      return fields.filter((f) => !f.editHide);
    }
    return fields.filter((f) => !f.viewHide);
  }

  getFieldsToDisplayPerRow(): FieldConfigDTO[][] {
    const fieldsToDisplay: any[] = this.getFieldsToDisplay();
    const number = Math.ceil(fieldsToDisplay.length / 2);
    if (number === fieldsToDisplay.length || this.config.columns === 1) {
      return this.transposeArray([fieldsToDisplay]);
    }
    // transposing so we have array of rows instead of columns
    return this.transposeArray([fieldsToDisplay.slice(0, number), fieldsToDisplay.slice(number)]);
  }

  private transposeArray(array: any[][]): any[][] {
    return array.reduce((r, a) => a.map((v, i) => [...(r[i] || []), v]), []);
  }

  isIgnored(field: FieldConfigDTO, data: any) {
    if (field.ignoreIfNotPresent && EntityDetailsMode.VIEW === this.mode) {
      return !data || data === '' || isObjEmpty(data) || data?.length === 0;
    }

    return false;
  }

  emitLoadingFalse() {
    this.loadingEmitter.emit(false);
  }

  transform(style: string): SafeStyle {
    if (style === undefined) {
      return '';
    }
    return this.domSanitizer.bypassSecurityTrustStyle(style);
  }
}
